<template>
  <sp-text tag="label" class="sp-label" @click="onClick"><slot></slot></sp-text>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, useHost, watchPostEffect } from "vue";

// The rendered `<label>` lives two shadow roots below the host, so its `for` can never reach a
// control in the tree the element itself sits in. The host does the labelling instead: it resolves
// the target in its own root, names it by id reference, and forwards a click.

const props = defineProps({
  /**
   * Id of the control this labels, resolved in the tree the element itself sits in.
   *
   * @type {String}
   * @default undefined
   */
  for: {
    type: String,
    default: undefined,
  },
});

const host = useHost();

// The control named at the last resolution, so the reference is withdrawn when `for` moves.
const labelled = ref(null);

watchPostEffect(() => nameTarget(resolveTarget(props.for)));

// `resolveTarget` is a DOM read, which no effect can depend on, and a label renders before a
// control that follows it in the same root. The retry catches the control the first pass missed.
onMounted(() => nextTick(retryIfUnresolved));

onBeforeUnmount(releaseTarget);

function retryIfUnresolved() {
  if (labelled.value || !props.for) {
    return;
  }

  nameTarget(resolveTarget(props.for));
}

function nameTarget(target) {
  if (target === labelled.value) {
    return;
  }

  releaseTarget();

  if (target) {
    labelled.value = target;
    const existing = target.getAttribute("aria-labelledby");
    target.setAttribute("aria-labelledby", [labelId(), existing].filter(Boolean).join(" "));
  }
}

function releaseTarget() {
  const target = labelled.value;

  if (!target) {
    return;
  }

  const remaining = (target.getAttribute("aria-labelledby") ?? "")
    .split(/\s+/)
    .filter((id) => id && id !== host.id)
    .join(" ");

  if (remaining) {
    target.setAttribute("aria-labelledby", remaining);
  } else {
    target.removeAttribute("aria-labelledby");
  }

  labelled.value = null;
}

/**
 * The host's own id, generated when it has none so the reference has something to point at.
 *
 * @returns {String}
 */
function labelId() {
  if (!host.id) {
    host.id = `sp-label-${Math.random().toString(36).slice(2, 10)}`;
  }

  return host.id;
}

/**
 * The control this labels: the `for` target resolved in the element's own root, or failing that
 * the first labelable element it wraps, the way a native label associates by containment.
 *
 * @param {String|undefined} id
 * @returns {HTMLElement|null}
 */
function resolveTarget(id) {
  if (id) {
    return host.getRootNode()?.getElementById?.(id) ?? null;
  }

  return host.querySelector("input, select, textarea, [data-sp-labelable]") ?? null;
}

function onClick(event) {
  const target = resolveTarget(props.for);

  // A click that lands on the control needs no forwarding, and a click that ends a text selection
  // is not an activation — a native label suppresses both.
  if (!target || event.composedPath().includes(target) || window.getSelection()?.toString()) {
    return;
  }

  target.focus?.();

  // A checkable control is toggled by its label; everything else only takes focus.
  if ("checked" in target && !target.disabled) {
    target.click();
  }
}
</script>

<style>
:host {
  display: inline;
  margin: 0;
}

/* The label forwards clicks to the control it names, so it reads as clickable.
   The rendered <label> takes `cursor: default` from the UA stylesheet, and a
   declaration on the element beats any inherited value — hence the part rule.
   A host whose control is unavailable overrides the token. */
sp-text::part(base) {
  cursor: var(--sp-comp-label-cursor, pointer);
}

:host([block]:not([block="false"])) {
  display: block;
}
</style>
